import { Button, Divider, Form, Input, Space } from "antd"
import { Formik } from "formik"
import React, { FC, useContext, useEffect, useMemo, useState } from "react"
import { Link, useHistory, useLocation } from "react-router-dom"
import { EndUserPasswordlessLoginInputType, ThirdPartyLoginMutation, useStartPasswordlessLoginMutation, useThirdPartyLoginMutation } from "../../generated/graphql"
import { createInputHelpers } from "../../helpers/createInputHelpers"
import { MainContext, SettingsContext } from "../../layouts/Main/MainLayout"
import { URL_FORGOT_PASSWORD, URL_LOGIN_COMPLETE_PASSWORDLESS_EMAIL, URL_REGISTER } from "../../models/url"
import LayoutForm from "../../views/LayoutForm/LayoutForm"
import LoginContainer from "../../views/LoginContainer/LoginContainer"
import { buildLoginPanelValidationSchema, LoginPanelValidationSchema } from "./LoginPanelValidationSchema"
import { blue, grey } from "@ant-design/colors"
import { SendOutlined } from "@ant-design/icons"
import ErrorPanel, { KnownError } from "../../errors/ErrorPanel"
import { LocationState } from "../../types"
import { useTranslation } from "react-i18next"
import * as Sentry from "@sentry/react"
import { getGraphQLErrorMessage } from "../../helpers/sentry"
import { safeStorage } from "../../helpers/safeStorage"
import { clearPersistedLoginFromUrl, getCourseIdFromLoginFromUrl, persistLoginFromUrl, resolveLoginFromUrl } from "./loginFromUrl"

const maxPasswordTries = 3

type LoginPanelProps = { setToken: (token: string) => void; boxed?: boolean; email?: string; parentUrl: string }

const LoginPanel: FC<LoginPanelProps> = ({ setToken, boxed = true, email, parentUrl }) => {
    const history = useHistory()
    const location = useLocation<LocationState>()
    const { t, i18n } = useTranslation()
    const [wrongPasswordCount, setWrongPasswordCount] = useState(0)
    const { userAuthDate } = useContext(MainContext)
    const isLoggedIn = !!safeStorage.getItem("loggedInUser")
    const [error, setError] = useState<string | undefined>()
    // Priorität: Location-State > persistierter Wert (Magic-Link verliert den State) > URL_BASE
    const fromUrl = resolveLoginFromUrl(location.state?.fromUrl)

    // fromUrl beim Start des Login-Flows persistieren — beim Passwordless-Login öffnet
    // der E-Mail-Link eine neue Navigation und der Location-State geht verloren.
    const stateFromUrlPathname = location.state?.fromUrl?.pathname
    useEffect(() => {
        persistLoginFromUrl(location.state?.fromUrl)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateFromUrlPathname])

    const appSettingsFromContext = useContext(SettingsContext)

    const { passwordlessLoginEnabled } = appSettingsFromContext

    useEffect(() => {
        if (wrongPasswordCount >= maxPasswordTries) {
            setError("errorPassword")
        }
    }, [wrongPasswordCount])

    const [userLogin, { data: userLoginData, loading: userLoginLoading }] = useThirdPartyLoginMutation()

    const thirdPartyLogin: ThirdPartyLoginMutation["thirdPartyLogin"] | undefined = userLoginData?.thirdPartyLogin

    useEffect(() => {
        if (thirdPartyLogin) {
            const { accessToken, refreshToken, booker, id } = thirdPartyLogin

            const attendee = booker?.attendees?.[0]

            if (accessToken && refreshToken && booker && id) {
                setToken(accessToken)
                // console.log("TOKEN WAS SET => ", thirdPartyLogin.accessToken)
                safeStorage.setItem("accessToken", accessToken)
                safeStorage.setItem("refreshToken", refreshToken)
                safeStorage.setItem("booker", JSON.stringify(booker))
                safeStorage.setItem("loggedInUser", id)
                if (attendee) {
                    safeStorage.setItem("attendeeId", JSON.stringify(attendee.id))
                }
                // Dispatch custom event für localStorage-Änderungen im App.tsx
                window.dispatchEvent(new Event('localStorageChange'))
                if (userAuthDate) {
                    userAuthDate(new Date())
                }
                const state: any = history?.location?.state
                const referer = state?.referer
                console.log("referer: ", referer)
                // Persistierte fromUrl ist verbraucht — entfernen, damit kein
                // späterer Login an einem veralteten Ziel landet.
                clearPersistedLoginFromUrl()
                history.push(fromUrl)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thirdPartyLogin])

    useEffect(() => {
        if (isLoggedIn) {
            clearPersistedLoginFromUrl()
            history.push(fromUrl)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [/* fromUrl, */ history, isLoggedIn])

    const knownErrors: Array<KnownError> = [
        {
            key: "User not found",
            title: t("login.errors.userNotFound.title", "Benutzer nicht gefunden"),
            msg: t("login.errors.userNotFound.msg", "Wir haben leider keine(n) Nutzer:in mit dieser E-Mail-Adresse gefunden."),
        },
        { key: "errorNotFound", title: t("login.errors.errorNotFound.title", "Unbekannter Fehler"), msg: t("login.errors.errorNotFound.msg", "Es gab einen unbekannten Fehler beim Login") },
        { key: "userLoginError", title: t("login.errors.userLoginError.title", "Fehler beim Login"), msg: t("login.errors.userLoginError.msg", "Es tut uns leid, es gab einen Fehler beim Login") },
        { key: "wrongPassword", title: t("login.errors.wrongPassword.title", "Falsches Passwort"), msg: t("login.errors.wrongPassword.msg", "Das eingegebene Passwort ist falsch.") },
        {
            // Backend-Vertrag (LoginErrorMessages.NO_CUSTOMER_ACCOUNT): FusionAuth-Login ok, aber kein
            // Kundenkonto zur E-Mail — typisch ein Mitarbeiter-Zugang, den nur der Admin als Kunde anlegen kann.
            key: "noCustomerAccount",
            title: t("login.errors.noCustomerAccount.title", "Kein Kundenkonto für diese E-Mail-Adresse"),
            msg: t(
                "login.errors.noCustomerAccount.msg",
                "Diese E-Mail-Adresse gehört zu einem Mitarbeiter-Zugang, zu dem noch kein Kundenkonto existiert. Bitte wende dich an den Anbieter: Er kann dich in der Verwaltung als Kunde anlegen. Danach kannst du dich hier mit deinem Passwort anmelden."
            ),
        },
        {
            key: "userNotFound",
            title: t("login.errors.userNotFound.title", "Benutzer nicht gefunden"),
            msg: t("login.errors.userNotFound.msg", "Wir haben leider keine(n) Nutzer:in mit dieser E-Mail-Adresse gefunden."),
        },
        { key: "missingUserName", title: t("login.errors.missingUserName.title", "Benutzername fehlt"), msg: t("login.errors.missingUserName.msg", "Bitte geben Sie Ihren Benutzernamen ein.") },
        {
            key: "missingPassword",
            title: t("login.errors.missingPassword.title", "Passwort fehlt"),
            msg: t("login.errors.missingPassword.msg", "Bitte geben Sie Ihr Passwort ein um sich anzumelden."),
        },
        { key: "missingBoth", title: t("login.errors.missingBoth.title", "Keine Daten"), msg: t("login.errors.missingBoth.msg", "Bitte gib Daten ein.") },
        {
            key: "errorPassword",
            title: t("login.errors.errorPassword.title", "Zu viele Fehlversuche"),
            msg: t("login.errors.errorPassword.msg", `Du hast dein Passwort ${maxPasswordTries} mal falsch eingegeben.\nBitte versuche dein Passwort zurückzusetzen.`),
        },
    ]

    const [startPasswordLessLoginFlow] = useStartPasswordlessLoginMutation()

    const startPasswordlessLogin = async ({ username }: { username: string }) => {
        console.log("startPasswordlessLogin")
        if (!username || username === "") {
            setError("missingUserName")
            return
        }

        const courseId = getCourseIdFromLoginFromUrl(fromUrl)
        const endUserPasswordlessLoginInput: EndUserPasswordlessLoginInputType = {
            companyName: "test",
            redirectUrl: parentUrl,
            email: username,
            ...(courseId ? { courseId } : {}),
        }

        startPasswordLessLoginFlow({
            variables: {
                endUserPasswordlessLoginInput,
            },
        })
            .then((res) => {
                if (res.data?.passwordlessLoginEndUserApp) {
                    history.push({
                        pathname: URL_LOGIN_COMPLETE_PASSWORDLESS_EMAIL.replace(":email", username),
                        state: { fromUrl },
                    })
                }
            })
            .catch((error: any) => {
                const rawErrorMessage = getGraphQLErrorMessage(error) || ""
                console.log("rawErrorMessage => ", rawErrorMessage)
                if (rawErrorMessage.includes("User not found") || rawErrorMessage.includes("Benutzer nicht gefunden")) {
                    setError("User not found")
                } else {
                    Sentry.captureException(error)
                }
            })
    }

    const inactiveStyle = (isMed: boolean) => {
        if (!isMed) {
            return { color: blue[5], borderColor: blue[3] }
        }

        return undefined
    }

    const validationSchema = useMemo(() => buildLoginPanelValidationSchema(t), [t, i18n.language])

    const renderContent = () => {
        return (
            <>
                <Formik
                    initialValues={{
                        username: email || "",
                        // username: "",
                        password: "",
                    }}
                    validationSchema={validationSchema}
                    onSubmit={async ({ username, password }) => {
                        try {
                            await userLogin({
                                variables: {
                                    thirdPartyLoginInput: { username, password },
                                },
                            })
                        } catch (error: any) {
                            console.log("error => ", error)
                            const rawErrorMessage = getGraphQLErrorMessage(error) || ""
                            if (rawErrorMessage.includes("You must specify the [password] property.")) {
                                setError("missingPassword")
                            } else if (rawErrorMessage.includes("No customer account")) {
                                setError("noCustomerAccount")
                            } else if (rawErrorMessage.includes("User not found")) {
                                setError("userNotFound")
                            } else if (rawErrorMessage.includes("Wrong password")) {
                                setWrongPasswordCount(wrongPasswordCount + 1)
                                if (wrongPasswordCount >= maxPasswordTries) {
                                    setError("errorPassword")
                                } else {
                                    setError("wrongPassword")
                                }
                            } else {
                                setError("errorNotFound")
                            }
                        }
                    }}
                >
                    {(formikProps) => {
                        const { handleSubmit, setFieldValue } = formikProps

                        // const { values } = formikProps
                        const { handleBlur, handleTextInputChange, validateStatus, help } = createInputHelpers(formikProps)

                        const loginFields = Array<"username" | "password">("username", "password")

                        const isValidEmail = formikProps.values?.username && formikProps.values?.username !== "" && formikProps.errors.username === undefined

                        return (
                            <LayoutForm onSubmitCapture={handleSubmit} columns={1}>
                                <Space direction='vertical' /* size='middle' */ size={7}>
                                    {/* {showErrorPanel(errorType)} */}
                                    {error && <ErrorPanel error={error} knownErrors={knownErrors} />}
                                    {wrongPasswordCount <= maxPasswordTries && (
                                        <>
                                            <Form.Item validateStatus={validateStatus("username")} help={help("username")} label={t("login.emailAddress", "E-Mail-Adresse")} required>
                                                <Input
                                                    // placeholder='Benutzername (e-mail)'
                                                    value={formikProps.values?.username ?? email}
                                                    placeholder={t("login.emailAddress", "E-Mail-Adresse")}
                                                    name='username'
                                                    onChange={handleTextInputChange("username")}
                                                    status={validateStatus(loginFields)}
                                                    onBlur={handleBlur("username")}
                                                    data-testid='login-email-input'
                                                />
                                            </Form.Item>
                                            {/* {isValidEmail && ( */}
                                            <>
                                                {passwordlessLoginEnabled && isValidEmail && (
                                                    <>
                                                        <Button
                                                            onClick={async () =>
                                                                startPasswordlessLogin({
                                                                    username: formikProps.values?.username,
                                                                })
                                                            }
                                                            style={{
                                                                marginTop: 5,
                                                                ...inactiveStyle(Boolean(formikProps.values?.username)),
                                                            }}
                                                            // type='primary'
                                                            type={Boolean(formikProps.values?.username) ? "primary" : "default"}
                                                            block
                                                        >
                                                            {t("login.passwordlessLogin", "Passwortloser Login")}
                                                            <SendOutlined />
                                                        </Button>
                                                        <Divider
                                                            children={"oder"}
                                                            plain
                                                            style={{
                                                                lineHeight: 0,
                                                                borderColor: "lightgrey",
                                                                color: grey[5],
                                                                marginTop: 20,
                                                            }}
                                                        />
                                                    </>
                                                )}
                                                {((passwordlessLoginEnabled && isValidEmail) || !passwordlessLoginEnabled) && (
                                                    <div>
                                                        <Form.Item
                                                            style={{ marginTop: 15 }}
                                                            colon={false}
                                                            validateStatus={validateStatus("password")}
                                                            help={help("password")}
                                                            // required
                                                            // label='Passwort'
                                                            label={
                                                                <div
                                                                    style={{
                                                                        display: "flex",
                                                                        justifyContent: "space-between",
                                                                        margin: 0,
                                                                        padding: 0,
                                                                        width: "300px",
                                                                        alignItems: "baseline",
                                                                    }}
                                                                >
                                                                    <div>{t("login.password", "Passwort")}</div>
                                                                    <Button
                                                                        size='small'
                                                                        type='link'
                                                                        style={{
                                                                            fontSize: "0.8rem",
                                                                            fontWeight: 400,
                                                                            padding: 0,
                                                                        }}
                                                                        onClick={() => history.push(URL_FORGOT_PASSWORD)}
                                                                    >
                                                                        {t("login.forgotPassword", "Passwort vergessen?")}
                                                                    </Button>
                                                                </div>
                                                            }
                                                        >
                                                            <Input
                                                                data-testid='login-password-input'
                                                                placeholder={t("login.password", "Passwort")}
                                                                name='password'
                                                                type='password'
                                                                onChange={(event) => setFieldValue("password", event.target.value, true)}
                                                                onBlur={handleBlur("password")}
                                                            />
                                                        </Form.Item>
                                                    </div>
                                                )}
                                                <Button
                                                    data-testid='login-with-password-button'
                                                    type={Boolean(formikProps.values?.username && formikProps.values?.password) ? "primary" : "default"}
                                                    htmlType='submit'
                                                    block
                                                    loading={userLoginLoading}
                                                    style={{
                                                        marginTop: 10,
                                                        ...inactiveStyle(Boolean(formikProps.values?.username && formikProps.values?.password)),
                                                    }}
                                                >
                                                    {t("login.loginWithPassword", "Mit Passwort anmelden")}
                                                </Button>
                                            </>
                                            {/* )} */}
                                        </>
                                    )}
                                </Space>
                                {wrongPasswordCount < maxPasswordTries ? (
                                    <>
                                        <Divider
                                            children={t("login.newHere", "Neu bei uns")}
                                            plain
                                            style={{
                                                lineHeight: 0,
                                                borderColor: "lightgrey",
                                                color: grey[5],
                                                marginTop: 8,
                                            }}
                                        />
                                        <Link to={{ pathname: URL_REGISTER, state: { fromUrl } }}>
                                            <Button
                                                type='link'
                                                style={{
                                                    fontWeight: 600,
                                                    fontSize: "0.9rem",
                                                    marginBottom: 4,
                                                    marginTop: 8,
                                                }}
                                            >
                                                {t("login.registerNow", "Jetzt registrieren")}
                                            </Button>
                                        </Link>
                                    </>
                                ) : (
                                    <>
                                        <Button type='link' onClick={() => history.push(URL_FORGOT_PASSWORD)}>
                                            Passwort zurücksetzen?
                                        </Button>
                                    </>
                                )}
                            </LayoutForm>
                        )
                    }}
                </Formik>
            </>
        )
    }
    if (!boxed) {
        return renderContent()
    }

    return <LoginContainer title={t("login.title", "Anmelden")} size='xs' content={renderContent()} />
}

export default LoginPanel
